import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  GetAllMarketContractsAction,
  AddMarketContractAction,
  UpdateMarketContractAction,
  DeleteMarketContractAction,
} from './market-contracts.actions';
import { IMarketContract } from '../../core/interfaces/market-contracts.interface';
import { MarketContractsService } from '../../core/services/market-contracts/market-contracts.service';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { EnovaAlertsComponent } from '../../core/utils/enova-alerts/enova-alerts.component';

export interface MarketContractsStateModel {
  marketContracts: IMarketContract[];
}

@State<MarketContractsStateModel>({
  name: 'marketContracts',
  defaults: {
    marketContracts: [],
  },
})
@Injectable()
export class MarketContractsState {
  @Selector() static GetAllData(
    state: MarketContractsStateModel
  ): IMarketContract[] {
    return state?.marketContracts ? state?.marketContracts : [];
  }

  constructor(
    private marketContractsService: MarketContractsService,
    private dialog: MatDialog
  ) {}

  @Action(GetAllMarketContractsAction)
  async GetAllMarketContractsAction(
    ctx: StateContext<MarketContractsStateModel>
  ) {
    try {
      const resp = await this.marketContractsService.getAllMarketContracts();
      if (resp && resp.length) {
        ctx.patchState({
          marketContracts: resp,
        });
      } else {
        ctx.patchState({
          marketContracts: [],
        });
      }
    } catch (error) {
      console.error('Error loading market contracts:', error);
      ctx.patchState({
        marketContracts: [],
      });
    }
  }

  @Action(AddMarketContractAction)
  async AddMarketContractAction(
    ctx: StateContext<MarketContractsStateModel>,
    { payload }: AddMarketContractAction
  ) {
    try {
      const result =
        await this.marketContractsService.createUpdateMarketContract(payload);
      if (result.status) {
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'success',
            text: 'El contrato de mercado secundario se ha creado de forma exitosa!',
            hiddeCancelBtn: true,
          },
        });
        await firstValueFrom(
          ctx.dispatch(new GetAllMarketContractsAction())
        );
      } else {
        throw result.err;
      }
    } catch (err: any) {
      let errorText = 'Ha ocurrido un error al crear el contrato.';
      const details = err?.error?.details || err?.details;
      if (details) {
        if (typeof details === 'string') {
          errorText = details;
        } else if (Array.isArray(details) && details.length > 0) {
          errorText = details.join('\n');
        }
      }
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'error',
          text: errorText,
          hiddeCancelBtn: true,
        },
      });
    }
  }

  @Action(UpdateMarketContractAction)
  async UpdateMarketContractAction(
    ctx: StateContext<MarketContractsStateModel>,
    { payload }: UpdateMarketContractAction
  ) {
    try {
      const result =
        await this.marketContractsService.createUpdateMarketContract(
          payload.data,
          payload.uuid
        );
      if (result.status) {
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'success',
            text: 'El contrato de mercado secundario se ha actualizado de forma exitosa!',
            hiddeCancelBtn: true,
          },
        });
        await firstValueFrom(
          ctx.dispatch(new GetAllMarketContractsAction())
        );
      } else {
        throw result.err;
      }
    } catch (err: any) {
      let errorText = 'Ha ocurrido un error al actualizar el contrato.';
      const details = err?.error?.details || err?.details;
      if (details) {
        if (typeof details === 'string') {
          errorText = details;
        } else if (Array.isArray(details) && details.length > 0) {
          errorText = details.join('\n');
        }
      }
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'error',
          text: errorText,
          hiddeCancelBtn: true,
        },
      });
    }
  }

  @Action(DeleteMarketContractAction)
  async DeleteMarketContractAction(
    ctx: StateContext<MarketContractsStateModel>,
    { payload }: DeleteMarketContractAction
  ) {
    try {
      const deleted =
        await this.marketContractsService.deleteMarketContract(payload);
      if (deleted) {
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'success',
            text: 'El contrato de mercado secundario se ha eliminado de forma exitosa!',
            hiddeCancelBtn: true,
          },
        });
        await firstValueFrom(
          ctx.dispatch(new GetAllMarketContractsAction())
        );
      }
    } catch (err: any) {
      let errorText = 'Ha ocurrido un error al eliminar el contrato.';
      const details = err?.error?.details || err?.details;
      if (details) {
        if (typeof details === 'string') {
          errorText = details;
        } else if (Array.isArray(details) && details.length > 0) {
          errorText = details.join('\n');
        }
      }
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'error',
          text: errorText,
          hiddeCancelBtn: true,
        },
      });
    }
  }
}
